import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { createChart, ColorType, CrosshairMode, IChartApi, ISeriesApi, CandlestickData, HistogramData, MouseEventParams, TickMarkType } from 'lightweight-charts';
import { CandleData, Timeframe } from '../types';
import debug from '../utils/debug';
import { getMarketSession, MARKET_SESSION_MINUTES } from '../utils/tradingHours';
import { LastPriceCountdownOverlay, type LastPriceCountdownOverlayHandle } from './LastPriceCountdownOverlay';
import { LastPriceCountdownOverlayController, type LastPriceCountdownOverlayControllerHandle } from './LastPriceCountdownOverlayController';

// Tick data structure for direct updates
export interface TickData {
  symbol: string;
  price: number;
  timestamp: number; // Unix timestamp in seconds
  volume?: number;
  session?: 'pre' | 'regular' | 'after' | 'closed';
}

// Imperative handle for direct tick updates
export interface LiveChartHandle {
  updateFromTick: (tick: TickData) => void;
  getLastCandle: () => CandleData | null;
  getCandleCount: () => number;
}

interface LiveChartProps {
  data: CandleData[];
  ticker: string;
  timeframe?: Timeframe;
  onLoadMore?: (oldestTime: number) => void;
  isFetchingMore?: boolean;
  hasMoreHistory?: boolean;
  onTickerChange?: (newTicker: string) => void;
  onTimeframeChange?: (newTimeframe: Timeframe) => void;
  isLoading?: boolean;
  replayMode?: 'off' | 'selecting' | 'active';
  onChartClick?: (timestamp: number) => void;
  timezone?: string;
  onTimezoneChange?: (newTimezone: string) => void;
  chartId?: string;
  preMarketTintColor?: string;
  regularSessionTintColor?: string;
  afterHoursTintColor?: string;
  // Performance optimization flags
  enableDirectTickUpdates?: boolean; // Enable direct chart updates bypassing React state
  tickUpdateThrottle?: number; // Throttle state updates (ms), 0 = no throttle
}

const LiveChart = forwardRef<LiveChartHandle, LiveChartProps>(({ 
    data, 
    ticker, 
    timeframe = '1h', 
    onLoadMore, 
    isFetchingMore = false,
    hasMoreHistory = true,
    onTickerChange,
    onTimeframeChange,
    isLoading = false,
    replayMode = 'off',
    onChartClick,
    timezone = 'America/New_York',
    onTimezoneChange,
    chartId,
    preMarketTintColor = 'rgba(56, 189, 248, 0.06)',
    regularSessionTintColor = 'rgba(34, 197, 94, 0.10)',
    afterHoursTintColor = 'rgba(249, 115, 22, 0.06)',
    enableDirectTickUpdates = true,
    tickUpdateThrottle = 1,
}, ref) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const fetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Internal candle data cache for direct updates
  const candleDataRef = useRef<CandleData[]>([]);
  const timeframeRef = useRef<Timeframe>(timeframe);
  const replayModeRef = useRef(replayMode);
  const isDisposingRef = useRef(false);
  const isUpdatingFromTickRef = useRef(false);
  const lastStateUpdateRef = useRef<number>(0);
  
  const [isEditing, setIsEditing] = useState(false);
  const [localTicker, setLocalTicker] = useState(ticker);
  const [tooltipData, setTooltipData] = useState<{ price: number; volume: number } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [seriesReady, setSeriesReady] = useState(false);
  const lastPriceOverlayRef = useRef<LastPriceCountdownOverlayHandle | null>(null);
  const lastPriceOverlayControllerRef = useRef<LastPriceCountdownOverlayControllerHandle | null>(null);

  const currentTickerRef = useRef<string>(ticker);
  const currentTimeframeRef = useRef<string>(timeframe);
  const currentTimezoneRef = useRef<string>(timezone);
  const dataRef = useRef<CandleData[]>(data);
  const onLoadMoreRef = useRef(onLoadMore);
  const isFetchingMoreRef = useRef(isFetchingMore);
  const hasMoreHistoryRef = useRef(hasMoreHistory);

  const [preMarketRects, setPreMarketRects] = useState<{ left: number; width: number }[]>([]);
  const [regularRects, setRegularRects] = useState<{ left: number; width: number }[]>([]);
  const [afterHoursRects, setAfterHoursRects] = useState<{ left: number; width: number }[]>([]);

  // Update refs
  useEffect(() => {
      dataRef.current = data;
      if (data.length > 0) {
        candleDataRef.current = data;
      }
      onLoadMoreRef.current = onLoadMore;
      isFetchingMoreRef.current = isFetchingMore;
      hasMoreHistoryRef.current = hasMoreHistory;
      timeframeRef.current = timeframe;
      replayModeRef.current = replayMode;
  }, [data, onLoadMore, isFetchingMore, hasMoreHistory, timeframe, replayMode]);

  useEffect(() => {
      if (!isEditing) {
        setLocalTicker(ticker);
      }
  }, [ticker, isEditing]);

  const getTimestampSeconds = (time: unknown): number | null => {
    if (typeof time === 'number') return time;
    if (time && typeof time === 'object' && 'year' in time && 'month' in time && 'day' in time) {
      const t = time as { year: number; month: number; day: number };
      return Math.floor(Date.UTC(t.year, t.month - 1, t.day) / 1000);
    }
    return null;
  };

  const formatTooltipTime = (timestampSec: number): string => {
    const tf = timeframeRef.current;
    const tz = currentTimezoneRef.current;
    const date = new Date(timestampSec * 1000);

    const isHigherTimeframe = tf.endsWith('d') || tf.endsWith('w');

    if (isHigherTimeframe) {
      return date.toLocaleDateString('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    }

    return date.toLocaleTimeString('en-US', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getTimeframeSeconds = (tf: Timeframe): number => {
    switch (tf) {
      case '1m': return 60;
      case '5m': return 300;
      case '15m': return 900;
      case '30m': return 1800;
      case '1h': return 3600;
      case '4h': return 14400;
      case '1d': return 86400;
      case '1w': return 604800;
      default: return 60;
    }
  };

  // Timeframe aggregation helper
  const aggregateTickToTimeframe = (tickTimestamp: number, tf: Timeframe): number => {
    const intervals: { [key in Timeframe]: number } = {
      '1m': 60,
      '5m': 300,
      '15m': 900,
      '30m': 1800,
      '1h': 3600,
      '4h': 14400,
      '1d': 86400,
      '1w': 604800,
    };
    
    const intervalSeconds = intervals[tf];
    return Math.floor(tickTimestamp / intervalSeconds) * intervalSeconds;
  };

  // Imperative API for direct tick updates
  useImperativeHandle(ref, () => ({
    updateFromTick: (tick: TickData) => {
      const debugPerfTs = (typeof performance !== 'undefined' && typeof performance.now === 'function') ? performance.now() : undefined;
      // Skip during replay mode
      if (replayModeRef.current === 'active' || replayModeRef.current === 'selecting') {
        debug.log('[LiveChart] Skipping tick update - replay mode active', {
          symbol: tick.symbol,
          price: tick.price,
          timestamp: tick.timestamp,
        });
        return;
      }

      // Skip during disposal
      if (isDisposingRef.current || !candlestickSeriesRef.current || !chartInstanceRef.current) {
        debug.warn('[LiveChart] Skipping tick update - chart not ready', {
          hasChart: !!chartInstanceRef.current,
          hasSeries: !!candlestickSeriesRef.current,
          disposing: isDisposingRef.current,
          symbol: tick.symbol,
        });
        return;
      }

      const price = Number(tick.price);
      if (!Number.isFinite(price) || price <= 0) {
        debug.warn('[LiveChart] Skipping tick with invalid price', {
          symbol: tick.symbol,
          price: tick.price,
          timestamp: tick.timestamp,
        });
        return;
      }

      // Aggregate tick to timeframe boundary
      const aggregatedTime = aggregateTickToTimeframe(tick.timestamp, timeframeRef.current);
      const currentCandles = candleDataRef.current;

      if (currentCandles.length === 0) {
        // First candle
        const newCandle: CandleData = {
          time: aggregatedTime * 1000,
          open: price,
          high: price,
          low: price,
          close: price,
          volume: tick.volume || 0,
          dateStr: new Date(aggregatedTime * 1000).toISOString(),
        };

        candleDataRef.current = [newCandle];

        if (enableDirectTickUpdates) {
          try {
            isUpdatingFromTickRef.current = true;
            candlestickSeriesRef.current.update({
              time: aggregatedTime as any,
              open: price,
              high: price,
              low: price,
              close: price,
            });

            volumeSeriesRef.current?.update({
              time: aggregatedTime as any,
              value: newCandle.volume,
              color: newCandle.close >= newCandle.open ? 'rgba(16, 185, 129, 0.5)' : 'rgba(244, 63, 94, 0.5)',
            } as any);
            debug.log('[LiveChart] Created first candle via direct update', {
              symbol: tick.symbol,
              price,
              timestamp: tick.timestamp,
            });
          } catch (error) {
            debug.error('[LiveChart] Error creating first candle', error);
          } finally {
            isUpdatingFromTickRef.current = false;
          }
        }
      } else {
        const lastCandle = currentCandles[currentCandles.length - 1];
        const lastCandleTime = Math.floor(lastCandle.time / 1000);

        if (aggregatedTime === lastCandleTime) {
          // Update existing candle
          const updatedCandle: CandleData = {
            ...lastCandle,
            close: price,
            high: Math.max(lastCandle.high, price),
            low: Math.min(lastCandle.low, price),
            volume: lastCandle.volume + (tick.volume || 0),
          };

          candleDataRef.current = [...currentCandles.slice(0, -1), updatedCandle];

          if (enableDirectTickUpdates) {
            try {
              isUpdatingFromTickRef.current = true;
              candlestickSeriesRef.current.update({
                time: lastCandleTime as any,
                open: updatedCandle.open,
                high: updatedCandle.high,
                low: updatedCandle.low,
                close: updatedCandle.close,
              });

              volumeSeriesRef.current?.update({
                time: lastCandleTime as any,
                value: updatedCandle.volume,
                color: updatedCandle.close >= updatedCandle.open ? 'rgba(16, 185, 129, 0.5)' : 'rgba(244, 63, 94, 0.5)',
              } as any);
              debug.log('[LiveChart] Updated candle', {
                symbol: tick.symbol,
                price,
                timestamp: tick.timestamp,
              });
            } catch (error) {
              debug.error('[LiveChart] Error updating candle', error);
            } finally {
              isUpdatingFromTickRef.current = false;
            }
          }
        } else if (aggregatedTime > lastCandleTime) {
          // New candle
          const newCandle: CandleData = {
            time: aggregatedTime * 1000,
            open: price,
            high: price,
            low: price,
            close: price,
            volume: tick.volume || 0,
            dateStr: new Date(aggregatedTime * 1000).toISOString(),
          };

          candleDataRef.current = [...currentCandles, newCandle];

          // Keep only last 1000 candles
          if (candleDataRef.current.length > 1000) {
            candleDataRef.current = candleDataRef.current.slice(-1000);
          }

          if (enableDirectTickUpdates) {
            try {
              isUpdatingFromTickRef.current = true;
              candlestickSeriesRef.current.update({
                time: aggregatedTime as any,
                open: price,
                high: price,
                low: price,
                close: price,
              });

              volumeSeriesRef.current?.update({
                time: aggregatedTime as any,
                value: newCandle.volume,
                color: newCandle.close >= newCandle.open ? 'rgba(16, 185, 129, 0.5)' : 'rgba(244, 63, 94, 0.5)',
              } as any);
              debug.log('[LiveChart] Created new candle', {
                symbol: tick.symbol,
                price,
                timestamp: tick.timestamp,
              });
            } catch (error) {
              debug.error('[LiveChart] Error creating new candle', error);
            } finally {
              isUpdatingFromTickRef.current = false;
            }
          }
        } else {
          debug.log('[LiveChart] Ignoring old tick', {
            symbol: tick.symbol,
            price,
            timestamp: tick.timestamp,
          });
        }
      }

      // Throttled state sync (optional - for external state management)
      const now = Date.now();
      if (tickUpdateThrottle === 0 || now - lastStateUpdateRef.current > tickUpdateThrottle) {
        lastStateUpdateRef.current = now;
        // Could emit event here for parent to sync state if needed
      }

      if (lastPriceOverlayControllerRef.current) {
        lastPriceOverlayControllerRef.current.onTick(debugPerfTs);
      }
    },

    getLastCandle: () => {
      const candles = candleDataRef.current;
      return candles.length > 0 ? candles[candles.length - 1] : null;
    },

    getCandleCount: () => {
      return candleDataRef.current.length;
    },
  }), [enableDirectTickUpdates, tickUpdateThrottle]);

  useEffect(() => {
    if (!seriesReady) return;

    const id = window.setInterval(() => {
      if (replayModeRef.current === 'active' || replayModeRef.current === 'selecting') return;
      if (isDisposingRef.current || !candlestickSeriesRef.current || !volumeSeriesRef.current) return;
      if (getMarketSession() === 'closed') return;

      const candles = candleDataRef.current;
      if (candles.length === 0) return;

      const last = candles[candles.length - 1];
      const lastTimeSec = Math.floor(last.time / 1000);

      const tfSeconds = getTimeframeSeconds(timeframeRef.current);
      const nowSec = Math.floor(Date.now() / 1000);
      const targetBucketStart = Math.floor(nowSec / tfSeconds) * tfSeconds;

      if (targetBucketStart <= lastTimeSec) return;

      const carryPrice = Number(last.close);
      if (!Number.isFinite(carryPrice) || carryPrice <= 0) return;

      const newCandle: CandleData = {
        time: targetBucketStart * 1000,
        open: carryPrice,
        high: carryPrice,
        low: carryPrice,
        close: carryPrice,
        volume: 0,
        dateStr: new Date(targetBucketStart * 1000).toISOString(),
      };

      candleDataRef.current = [...candles, newCandle].slice(-1000);

      if (enableDirectTickUpdates) {
        try {
          isUpdatingFromTickRef.current = true;
          candlestickSeriesRef.current.update({
            time: targetBucketStart as any,
            open: newCandle.open,
            high: newCandle.high,
            low: newCandle.low,
            close: newCandle.close,
          });

          volumeSeriesRef.current.update({
            time: targetBucketStart as any,
            value: newCandle.volume,
            color: newCandle.close >= newCandle.open ? 'rgba(16, 185, 129, 0.5)' : 'rgba(244, 63, 94, 0.5)',
          } as any);
        } finally {
          isUpdatingFromTickRef.current = false;
        }
      }

      if (lastPriceOverlayControllerRef.current) {
        const debugPerfTs = (typeof performance !== 'undefined' && typeof performance.now === 'function') ? performance.now() : undefined;
        lastPriceOverlayControllerRef.current.refresh(debugPerfTs);
      }
    }, 1000);

    return () => window.clearInterval(id);
  }, [enableDirectTickUpdates, seriesReady]);

  // Session helpers (same as original)
  const computeSessionBlocks = React.useCallback((candles: CandleData[]) => {
    const estTz = 'America/New_York';

    const getMinutes = (tsMs: number) => {
      const d = new Date(tsMs);
      const hour = parseInt(d.toLocaleString('en-US', { hour: '2-digit', hour12: false, timeZone: estTz }), 10);
      const minute = parseInt(d.toLocaleString('en-US', { minute: '2-digit', timeZone: estTz }), 10);
      return hour * 60 + minute;
    };

    const PRE_START = MARKET_SESSION_MINUTES.PRE_MARKET_START;
    const PRE_END = MARKET_SESSION_MINUTES.MARKET_OPEN;
    const REG_START = MARKET_SESSION_MINUTES.MARKET_OPEN;
    const REG_END = MARKET_SESSION_MINUTES.MARKET_CLOSE;
    const AFTER_START = MARKET_SESSION_MINUTES.MARKET_CLOSE;
    const AFTER_END = MARKET_SESSION_MINUTES.AFTER_HOURS_END;

    type Block = { start: number; end: number };
    const sorted = [...candles].sort((a, b) => a.time - b.time);

    const buildBlocks = (predicate: (m: number) => boolean): Block[] => {
      const blocks: Block[] = [];
      let currentStart: number | null = null;
      let lastTs: number | null = null;

      for (const c of sorted) {
        const m = getMinutes(c.time);
        if (predicate(m)) {
          if (currentStart === null) currentStart = c.time;
          lastTs = c.time;
        } else {
          if (currentStart !== null && lastTs !== null) {
            blocks.push({ start: currentStart, end: lastTs });
          }
          currentStart = null;
          lastTs = null;
        }
      }
      if (currentStart !== null && lastTs !== null) {
        blocks.push({ start: currentStart, end: lastTs });
      }
      return blocks;
    };

    const preBlocks = buildBlocks((m) => m >= PRE_START && m < PRE_END);
    const regBlocks = buildBlocks((m) => m >= REG_START && m < REG_END);
    const afterBlocks = buildBlocks((m) => m >= AFTER_START && m < AFTER_END);

    return { preBlocks, regBlocks, afterBlocks };
  }, []);

  const recomputeSessionRects = React.useCallback(() => {
    if (!chartInstanceRef.current) return;
    const candles = candleDataRef.current;
    if (!candles.length) {
      setPreMarketRects([]);
      setRegularRects([]);
      setAfterHoursRects([]);
      return;
    }

    const { preBlocks, regBlocks, afterBlocks } = computeSessionBlocks(candles);
    const timeScale = chartInstanceRef.current.timeScale();

    const blocksToRects = (blocks: { start: number; end: number }[]) => {
      const rects: { left: number; width: number }[] = [];
      const anyTimeScale = timeScale as any;
      const barSpacing = typeof anyTimeScale.getBarSpacing === 'function' ? anyTimeScale.getBarSpacing() : 6;
      const halfBar = barSpacing/2 ;
      for (const block of blocks) {
        const startCoord = timeScale.timeToCoordinate((block.start / 1000) as any);
        const endCoord = timeScale.timeToCoordinate((block.end / 1000) as any);
        if (startCoord == null || endCoord == null) continue;
        const leftCenter = Math.min(startCoord, endCoord);
        const rightCenter = Math.max(startCoord, endCoord);
        // Bar-based adjustment: cover from left edge of first candle to right edge of last candle
        const left = leftCenter - halfBar;
        const right = rightCenter + halfBar;
        const width = right - left;
        if (width <= 0) continue;
        rects.push({ left, width });
      }
      return rects;
    };

    setPreMarketRects(blocksToRects(preBlocks));
    setRegularRects(blocksToRects(regBlocks));
    setAfterHoursRects(blocksToRects(afterBlocks));
  }, [computeSessionBlocks]);

  const handleTickerSubmit = (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      setIsEditing(false);
      if (localTicker && localTicker !== ticker && onTickerChange) {
          onTickerChange(localTicker.toUpperCase());
      } else {
          setLocalTicker(ticker);
      }
  };

  const handleTimeframeClick = (tf: Timeframe) => {
      if (onTimeframeChange && tf !== timeframe) {
          onTimeframeChange(tf);
      }
  };

  const handleTimezoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (onTimezoneChange) {
        onTimezoneChange(e.target.value);
      }
  };

  // Initialize Chart (same as original, keeping all the setup logic)
  useEffect(() => {
    if (!chartContainerRef.current) return;

    debug.info('[LiveChart] Initializing chart');
    
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#94a3b8',
        fontFamily: "'Inter', sans-serif",
      },
      grid: {
        vertLines: { color: '#1e293b' },
        horzLines: { color: '#1e293b' },
      },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      timeScale: {
        borderColor: '#334155',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 12,
        tickMarkFormatter: (time: number, tickMarkType: TickMarkType, locale: string) => {
            const date = new Date(time * 1000);
            switch (tickMarkType) {
                case TickMarkType.Year:
                    return date.toLocaleDateString(locale, { year: 'numeric', timeZone: timezone });
                case TickMarkType.Month:
                    return date.toLocaleDateString(locale, { month: 'short', timeZone: timezone });
                case TickMarkType.DayOfMonth:
                    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: timezone });
                case TickMarkType.Time:
                    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone });
                case TickMarkType.TimeWithSeconds:
                    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: timezone });
                default:
                    return date.toLocaleString(locale, { timeZone: timezone });
            }
        }
      },
      rightPriceScale: {
        borderColor: '#334155',
      },
      localization: {
          timeFormatter: (timestamp: number) => {
              return new Date(timestamp * 1000).toLocaleString('en-US', {
                  timeZone: timezone, 
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                  month: 'short',
                  day: 'numeric'
              });
          }
      },
      watermark: {
          visible: false
      }
    });

    const candlestickSeries = chart.addCandlestickSeries({
      upColor: '#10b981',
      downColor: '#f43f5e',
      borderVisible: false,
      wickUpColor: '#10b981',
      wickDownColor: '#f43f5e',
      priceScaleId: 'right',
      lastValueVisible: false,
      priceLineVisible: true,
      autoscaleInfoProvider: (original: () => any) => {
        const res = original();
        if (res !== null) {
          const range = res.priceRange.maxValue - res.priceRange.minValue;
          res.priceRange.maxValue += range * 0.05;
          res.priceRange.minValue -= range * 0.05;
        }
        return res;
      },
    });

    const volumeSeries = chart.addHistogramSeries({
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: '',
      color: '#26a69a',
    });

    volumeSeries.priceScale().applyOptions({
      scaleMargins: {
        top: 0.7,
        bottom: 0,
      },
    });

    chartInstanceRef.current = chart;
    candlestickSeriesRef.current = candlestickSeries;
    volumeSeriesRef.current = volumeSeries;
    setSeriesReady(true);

    const timeScale = chart.timeScale();
    timeScale.subscribeVisibleLogicalRangeChange((newRange) => {
        if (newRange) {
            if (newRange.from < 10) {
                if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
                
                fetchDebounceRef.current = setTimeout(() => {
                    if (dataRef.current.length > 0 && 
                        !isFetchingMoreRef.current && 
                        onLoadMoreRef.current && 
                        hasMoreHistoryRef.current) {
                        
                        const oldestTime = dataRef.current[0]?.time || 0;
                        console.log(`[LiveChart] onLoadMore triggered: dataLen=${dataRef.current.length}, oldestTime=${oldestTime}, firstCandle=`, dataRef.current[0]);
                        onLoadMoreRef.current(oldestTime);
                    }
                }, 200);
            }
        }
        recomputeSessionRects();
    });

    const handleResize = () => {
      if (chartContainerRef.current && chartInstanceRef.current) {
        chartInstanceRef.current.applyOptions({ 
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight 
        });
      }
    };

    window.addEventListener('resize', handleResize);

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
      recomputeSessionRects();
    });
    
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    chart.subscribeCrosshairMove((param: MouseEventParams) => {
      if (!param.time || !param.point) {
        setTooltipData(null);
        setTooltipPosition(null);
        return;
      }

      const candleData = param.seriesData.get(candlestickSeries) as CandlestickData | undefined;
      const volumeData = param.seriesData.get(volumeSeries) as HistogramData | undefined;

      if (candleData && volumeData) {
        const timestampSec = getTimestampSeconds((candleData as any).time ?? param.time);
        if (timestampSec == null) {
          setTooltipData(null);
          setTooltipPosition(null);
          return;
        }
        setTooltipData({
          price: candleData.close,
          volume: volumeData.value,
        });
        setTooltipPosition({
          x: param.point.x,
          y: param.point.y
        });
      }
    });

    return () => {
      isDisposingRef.current = true;
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
      chart.remove();
      chartInstanceRef.current = null;
      candlestickSeriesRef.current = null;
      volumeSeriesRef.current = null;
      setSeriesReady(false);
      isDisposingRef.current = false;
    };
  }, [recomputeSessionRects, timezone]);

  // Timezone update effect
  useEffect(() => {
      if (chartInstanceRef.current && currentTimezoneRef.current !== timezone) {
          const newOptions = {
              localization: {
                  timeFormatter: (timestamp: number) => {
                      return new Date(timestamp * 1000).toLocaleString('en-US', {
                          timeZone: timezone,
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                          month: 'short',
                          day: 'numeric'
                      });
                  }
              },
              timeScale: {
                  tickMarkFormatter: (time: number, tickMarkType: TickMarkType, locale: string) => {
                      const date = new Date(time * 1000);
                      switch (tickMarkType) {
                          case TickMarkType.Year:
                              return date.toLocaleDateString(locale, { year: 'numeric', timeZone: timezone });
                          case TickMarkType.Month:
                              return date.toLocaleDateString(locale, { month: 'short', timeZone: timezone });
                          case TickMarkType.DayOfMonth:
                              return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: timezone });
                          case TickMarkType.Time:
                              return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone });
                          case TickMarkType.TimeWithSeconds:
                              return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: timezone });
                          default:
                              return date.toLocaleString(locale, { timeZone: timezone });
                      }
                  }
              }
          };
          
          chartInstanceRef.current.applyOptions(newOptions);
          currentTimezoneRef.current = timezone;
      }
  }, [timezone]);

  // Replay mode click handler
  useEffect(() => {
    if (!chartInstanceRef.current) return;
    
    const handleChartClick = (param: MouseEventParams) => {
        if (replayMode === 'selecting' && onChartClick) {
            let timestamp: number | undefined;

            if (param.time) {
                timestamp = (param.time as number) * 1000;
            } else if (param.point && chartInstanceRef.current) {
                 const timeScale = chartInstanceRef.current.timeScale();
                 const timeVal = timeScale.coordinateToTime(param.point.x);
                 if (timeVal) {
                     timestamp = (timeVal as number) * 1000;
                 }
            }

            if (timestamp) {
                onChartClick(timestamp);
            }
        }
    };

    chartInstanceRef.current.subscribeClick(handleChartClick);
    
    if (chartContainerRef.current) {
        chartContainerRef.current.style.cursor = replayMode === 'selecting' ? 'crosshair' : 'default';
    }
    
    return () => {
        if (chartInstanceRef.current) {
            chartInstanceRef.current.unsubscribeClick(handleChartClick);
        }
    };
  }, [replayMode, onChartClick]);

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (replayMode !== 'selecting' || !chartInstanceRef.current || !onChartClick) return;
      
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const timeScale = chartInstanceRef.current.timeScale();
      const timeVal = timeScale.coordinateToTime(x);
      if (timeVal) {
          const timestampSec = getTimestampSeconds(timeVal);
          if (timestampSec == null) return;
          onChartClick(timestampSec * 1000);
      }
  };

  const handleDoubleClick = () => {
      if (chartInstanceRef.current && candlestickSeriesRef.current) {
          const timeScale = chartInstanceRef.current.timeScale();
          const priceScale = chartInstanceRef.current.priceScale('right');
          
          timeScale.fitContent();
          
          priceScale.applyOptions({
              autoScale: true,
          });
          
          setTimeout(() => {
              timeScale.fitContent();
              
              chartInstanceRef.current?.resize(
                  chartContainerRef.current?.clientWidth || 0,
                  chartContainerRef.current?.clientHeight || 0
              );
          }, 50);
      }
  };

  const prevReplayModeRef = useRef(replayMode);

  // Update Data Effect - only when data prop changes (not from tick updates)
  useEffect(() => {
    if (!seriesReady || !candlestickSeriesRef.current || !volumeSeriesRef.current) return;

    // Skip if update is from tick (internal update)
    if (isUpdatingFromTickRef.current) return;

    const sourceData = data.length > 0 ? data : candleDataRef.current;
    if (sourceData.length === 0) return;

    const sortedData = [...sourceData].sort((a, b) => a.time - b.time);
    const uniqueData = [];
    const seen = new Set();
    
    for (const item of sortedData) {
        if (!seen.has(item.time)) {
            seen.add(item.time);
            uniqueData.push(item);
        }
    }

    const isValidCandle = (c: CandleData): boolean => {
      if (!Number.isFinite(c.time)) return false;
      if (!Number.isFinite(c.open) || !Number.isFinite(c.high) || !Number.isFinite(c.low) || !Number.isFinite(c.close)) return false;
      if (c.open <= 0 || c.high <= 0 || c.low <= 0 || c.close <= 0) return false;
      return true;
    };

    const filteredData = uniqueData.filter(isValidCandle);
    if (filteredData.length === 0) {
      return;
    }

    candleDataRef.current = filteredData;

    const candleChartData = filteredData.map(d => ({
      time: (d.time / 1000) as any,
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close,
    })) as CandlestickData[];

    const volumeChartData = filteredData.map(d => ({
      time: (d.time / 1000) as any,
      value: d.volume,
      color: d.close >= d.open ? 'rgba(16, 185, 129, 0.5)' : 'rgba(244, 63, 94, 0.5)',
    })) as HistogramData[];

    candlestickSeriesRef.current.setData(candleChartData);
    volumeSeriesRef.current.setData(volumeChartData);

    if (lastPriceOverlayControllerRef.current) {
      const debugPerfTs = (typeof performance !== 'undefined' && typeof performance.now === 'function') ? performance.now() : undefined;
      lastPriceOverlayControllerRef.current.refresh(debugPerfTs);
    }

    if (chartInstanceRef.current) {
        const timeScale = chartInstanceRef.current.timeScale();
        
        if (currentTickerRef.current !== ticker || currentTimeframeRef.current !== timeframe) {
            timeScale.fitContent();
            currentTickerRef.current = ticker;
            currentTimeframeRef.current = timeframe;
        } 
        else if (replayMode === 'active' && prevReplayModeRef.current !== 'active') {
             timeScale.fitContent();
        }
    }

    prevReplayModeRef.current = replayMode;
    recomputeSessionRects();

  }, [data, ticker, timeframe, replayMode, recomputeSessionRects, seriesReady]);

  return (
    <div className="relative w-full flex-1 bg-slate-900/50 border border-slate-800 group overflow-hidden flex flex-col">
       {!chartId && (
         <div className="absolute top-2 left-2 z-10 pointer-events-auto flex flex-col">
          <div className="flex items-center gap-2 bg-slate-900/90 px-2 py-1.5 rounded-md backdrop-blur-sm border border-slate-800 shadow-lg">
              {isEditing ? (
                  <form onSubmit={handleTickerSubmit} className="flex items-center">
                      <input 
                          type="text" 
                          value={localTicker}
                          onChange={(e) => setLocalTicker(e.target.value.toUpperCase())}
                          onBlur={() => handleTickerSubmit()}
                          autoFocus
                          className="text-base font-semibold text-slate-200 bg-slate-800 border border-emerald-500/50 rounded px-2 py-1 outline-none w-20 uppercase tracking-tight"
                      />
                  </form>
              ) : (
                  <input 
                      type="text" 
                      value={ticker}
                      onClick={() => replayMode === 'off' && setIsEditing(true)}
                      readOnly
                      disabled={replayMode !== 'off'}
                      className={`text-base font-semibold bg-slate-800 border rounded px-2 py-1 outline-none w-20 uppercase tracking-tight transition-colors ${
                          replayMode !== 'off' 
                              ? 'text-slate-500 border-slate-800 cursor-not-allowed opacity-60' 
                              : 'text-slate-200 border-slate-700 hover:border-slate-600 cursor-pointer'
                      }`}
                      title={replayMode !== 'off' ? 'Ticker changes disabled during replay mode' : 'Click to change ticker'}
                  />
              )}
              
              <select 
                  value={timeframe} 
                  onChange={(e) => replayMode === 'off' && handleTimeframeClick(e.target.value as Timeframe)}
                  disabled={replayMode !== 'off'}
                  className={`text-sm font-medium bg-slate-800 border rounded px-2 py-1 outline-none transition-colors appearance-none pr-6 ${
                      replayMode !== 'off'
                          ? 'text-slate-500 border-slate-800 cursor-not-allowed opacity-60'
                          : 'text-slate-200 border-slate-700 hover:border-slate-600 cursor-pointer'
                  }`}
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%23cbd5e1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.25rem center', backgroundSize: '0.75rem' }}
                  title={replayMode !== 'off' ? 'Timeframe changes disabled during replay mode' : ''}
              >
                  <option value="1m">1m</option>
                  <option value="5m">5m</option>
                  <option value="15m">15m</option>
                  <option value="30m">30m</option>
                  <option value="1h">1h</option>
                  <option value="4h">4h</option>
                  <option value="1d">1d</option>
                  <option value="1w">1w</option>
              </select>

          </div>
        
          <div className="flex items-center gap-2 mt-2 ml-1">
              {replayMode === 'active' ? (
                  <span className="text-[10px] text-amber-400 flex items-center gap-1.5 font-medium bg-amber-500/10 px-2 py-0.5 rounded-full w-fit border border-amber-500/20">
                      <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500"></span>
                      </span>
                      Replay Mode
                  </span>
              ) : (() => {
                  const session = getMarketSession();
                  if (session === 'closed') {
                    return (
                      <span className="text-[10px] text-slate-400 flex items-center gap-1.5 font-medium bg-slate-500/10 px-2 py-0.5 rounded-full w-fit border border-slate-500/30">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-slate-500"></span>
                        </span>
                        Market Closed
                      </span>
                    );
                  } else if (session === 'pre-market') {
                    return (
                      <span className="text-[10px] text-blue-400 flex items-center gap-1.5 font-medium bg-blue-500/10 px-2 py-0.5 rounded-full w-fit border border-blue-500/20">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500"></span>
                        </span>
                        Pre-Market
                      </span>
                    );
                  } else if (session === 'after-hours') {
                    return (
                      <span className="text-[10px] text-purple-400 flex items-center gap-1.5 font-medium bg-purple-500/10 px-2 py-0.5 rounded-full w-fit border border-purple-500/20">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple-500"></span>
                        </span>
                        After Hours
                      </span>
                    );
                  } else {
                    return (
                      <span className="text-[10px] text-emerald-500 flex items-center gap-1.5 font-medium bg-emerald-500/10 px-2 py-0.5 rounded-full w-fit border border-emerald-500/20">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                        </span>
                        Live Feed
                      </span>
                    );
                  }
                })()}
              
              {isFetchingMore && (
                  <span className="text-[10px] text-blue-400 flex items-center gap-1.5 font-medium bg-blue-500/10 px-2 py-0.5 rounded-full animate-pulse border border-blue-500/20">
                      Loading History...
                  </span>
              )}
          </div>
        </div>
       )}
      
      <div
        onClick={handleContainerClick}
        onDoubleClick={handleDoubleClick}
        data-testid="live-chart-container"
        data-test-last-candle-close={String(candleDataRef.current.length > 0 ? candleDataRef.current[candleDataRef.current.length - 1].close : '0')}
        className="flex-1 w-full h-full relative"
      >
        <div ref={chartContainerRef} className="absolute inset-0 z-0" />

        <LastPriceCountdownOverlay ref={lastPriceOverlayRef} />
        <LastPriceCountdownOverlayController
          ref={lastPriceOverlayControllerRef}
          overlayRef={lastPriceOverlayRef}
          getSeries={() => candlestickSeriesRef.current}
          getCandles={() => candleDataRef.current}
          getTimeframe={() => timeframeRef.current}
          replayMode={replayMode}
        />

        {preMarketRects.map((rect, idx) => (
          <div
            key={`pre-${idx}`}
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{
              left: rect.left,
              width: rect.width,
              backgroundColor: preMarketTintColor,
              zIndex: 1,
            }}
          />
        ))}
        {regularRects.map((rect, idx) => (
          <div
            key={`reg-${idx}`}
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{
              left: rect.left,
              width: rect.width,
              backgroundColor: regularSessionTintColor,
              zIndex: 1,
            }}
          />
        ))}
        {afterHoursRects.map((rect, idx) => (
          <div
            key={`after-${idx}`}
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{
              left: rect.left,
              width: rect.width,
              backgroundColor: afterHoursTintColor,
              zIndex: 1,
            }}
          />
        ))}
        {tooltipData && tooltipPosition && (
          <div
            className="absolute pointer-events-none z-50 bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg px-3 py-2 shadow-xl"
            style={{
              left: `${tooltipPosition.x + 15}px`,
              top: `${tooltipPosition.y - 60}px`,
            }}
          >
            <div className="text-xs space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Price:</span>
                <span className="text-emerald-400 font-semibold">${tooltipData.price.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Volume:</span>
                <span className="text-blue-400 font-semibold">{tooltipData.volume.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

LiveChart.displayName = 'LiveChart';

export default LiveChart;
